import { NextPageContext } from 'next';

import { equipment } from '@r1-frontend/api-domru/full-buy/v1/equipment';
import { IEquipment } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/Equipment.dto';
import { ICharacteristics } from '@r1-frontend/api-domru/full-buy/v1/equipment/dto/EquipmentProp.dto';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { EShopCategory, IShopItem } from '~/src/api/content/shop/getShopItems';
import { calculateEquipmentProps } from '~/src/api/content/shop/helpers';
import { initialProps } from '~/src/helpers/basePageFunc';
import getRequester from '~/src/helpers/getRequester';
import { nonPermanentRedirect } from '~/src/helpers/url';
import { selectCompanyName } from '~/src/store/selectors/city';

interface IParams {
    category: EShopCategory,
    basePath: string,
}

interface ICommonProps {
    city: ICurrentCity,
    companyName: string,
    category: EShopCategory,
    basePath: string,
}

export type TEquipmentDetailsProps = ICommonProps & ({
    products: IEquipment[],
    equipment: IEquipment,

    errorCode?: never,
} | {
    errorCode: number,

    products?: never,
    equipment?: never,
});

export const getItemDetailsInitialProps = async(options: NextPageContext, params: IParams): Promise<TEquipmentDetailsProps> => {
    await initialProps(options);

    const { category, basePath } = params;
    const { store, query: { itemId }, res, req } = options;
    const alias = Array.isArray(itemId) ? itemId[0] : (itemId ?? '');

    const state = store.getState();
    const { city: { currentCity: city, provider: { providerId } } } = state;
    const companyName = selectCompanyName(state);

    const commonProps: ICommonProps = { city, companyName, category, basePath };

    const doRequest = getRequester(req);

    const response = await doRequest.fullBuy<IEquipment[]>(
        equipment({
            providerId,
            categories: [category],
        }),
    );

    if (response.isSuccess) {
        const equipmentData = response.payload.find(product => product.alias === alias);

        if (!equipmentData) {
            nonPermanentRedirect({ res, location: basePath });

            return {
                errorCode: 404,
                ...commonProps,
            };
        } else {
            const { props, category: equipmentCategory } = equipmentData;

            const calculatedProps = calculateEquipmentProps({ props, category: equipmentCategory } as IShopItem)
                .reduce((acc: ICharacteristics[], prop) => {
                    const value = Array.isArray(prop.value) ? prop.value[0] : (prop.value ?? '');

                    if (value) {
                        acc.push({ ...prop, value, asIs: prop.asIs ?? 0 });
                    }

                    return acc;
                }, []);

            return {
                products: response.payload,
                equipment: { ...equipmentData, props: calculatedProps },
                ...commonProps,
            };
        }
    } else {
        nonPermanentRedirect({ res, location: basePath });

        return {
            errorCode: Number(response.error.code) || 404,
            ...commonProps,
        };
    }
};
